<script lang="ts" setup>
import { computed, type StyleValue } from 'vue'

import { type PFlexItemSize, PFlexSize, PFlexSticky } from './types'

type Props = {
  size?: PFlexItemSize
  sizeLaptop?: PFlexItemSize | null
  sizeDesktop?: PFlexItemSize | null
  minWidth?: number | null
  minWidthLaptop?: number | null
  minWidthDesktop?: number | null
  maxWidth?: number | null
  maxWidthLaptop?: number | null
  maxWidthDesktop?: number | null
  order?: number | null
  orderLaptop?: number | null
  orderDesktop?: number | null
  sticky?: PFlexSticky | null
}

const props = withDefaults(defineProps<Props>(), {
  size: PFlexSize.FILL,
  sizeLaptop: null,
  sizeDesktop: null,
  minWidth: null,
  minWidthLaptop: null,
  minWidthDesktop: null,
  maxWidth: null,
  maxWidthLaptop: null,
  maxWidthDesktop: null,
  order: null,
  orderLaptop: null,
  orderDesktop: null,
  sticky: null,
})

const sizeLaptop = computed<PFlexItemSize>(() => props.sizeLaptop ?? props.size)

const sizeDesktop = computed<PFlexItemSize>(() => props.sizeDesktop ?? sizeLaptop.value)

const minWidthLaptop = computed<number | null>(() => props.minWidthLaptop ?? props.minWidth)

const minWidthDesktop = computed<number | null>(() => props.minWidthDesktop ?? minWidthLaptop.value)

const maxWidthLaptop = computed<number | null>(() => props.maxWidthLaptop ?? props.maxWidth)

const maxWidthDesktop = computed<number | null>(() => props.maxWidthDesktop ?? maxWidthLaptop.value)

const order = computed<number>(() => props.order ?? 0)

const orderLaptop = computed<number>(() => props.orderLaptop ?? order.value)

const orderDesktop = computed<number>(() => props.orderDesktop ?? orderLaptop.value)

// In a row the size drives the main axis...
const flexFor = (size: PFlexItemSize): string => {
  if (typeof size === 'number') {
    return `0 0 ${size}px`
  }

  return size === PFlexSize.HUG ? '0 0 auto' : '1 1 0'
}

// ...while in a column the main axis is vertical and the size describes the width.
const widthFor = (size: PFlexItemSize): string => (typeof size === 'number' ? `${size}px` : 'auto')

// A fixed size doubles as its own constraint, an explicitly given one always wins.
const constraintFor = (explicit: number | null, size: PFlexItemSize, fallback: string): string => {
  if (explicit !== null) {
    return `${explicit}px`
  }

  return typeof size === 'number' ? `${size}px` : fallback
}

const variablesFor = (
  breakpoint: string,
  size: PFlexItemSize,
  minWidth: number | null,
  maxWidth: number | null,
  order: number,
): Record<string, number | string> => ({
  [`--ds-flex-item-flex-${breakpoint}`]: flexFor(size),
  [`--ds-flex-item-width-${breakpoint}`]: widthFor(size),
  [`--ds-flex-item-min-width-${breakpoint}`]: constraintFor(minWidth, size, 'auto'),
  [`--ds-flex-item-max-width-${breakpoint}`]: constraintFor(maxWidth, size, 'none'),
  [`--ds-flex-item-order-${breakpoint}`]: order,
})

const itemStyle = computed<StyleValue>(() => ({
  ...variablesFor('mobile', props.size, props.minWidth, props.maxWidth, order.value),
  ...variablesFor(
    'laptop',
    sizeLaptop.value,
    minWidthLaptop.value,
    maxWidthLaptop.value,
    orderLaptop.value,
  ),
  ...variablesFor(
    'desktop',
    sizeDesktop.value,
    minWidthDesktop.value,
    maxWidthDesktop.value,
    orderDesktop.value,
  ),
}))
</script>

<template>
  <div
    :style="itemStyle"
    :class="sticky !== null ? ['ds-flex-item-sticky', `ds-flex-item-sticky-${sticky}`] : null"
    class="ds-flex-item"
  >
    <slot />
  </div>
</template>

<style lang="scss" scoped>
@import '../../assets/scss/shared/_brand.module.scss';
@import '../../assets/scss/shared/mixins';

$sticky-background: var(--ds-flex-item-sticky-background, #{getColor('white')});

.ds-flex-item-sticky {
  position: sticky;
  background-color: $sticky-background;
  // Full row height, otherwise taller neighbours peek out below the pinned item.
  align-self: stretch;
}

// The box shadow paints over the row gap, so that the content scrolling underneath
// does not show through the strip next to the pinned item.
.ds-flex-item-sticky-start {
  left: 0;
  box-shadow: var(--ds-flex-row-gap, 0px) 0 0 0 $sticky-background;
}

.ds-flex-item-sticky-end {
  right: 0;
  box-shadow: calc(-1 * var(--ds-flex-row-gap, 0px)) 0 0 0 $sticky-background;
}

@mixin breakpoint-rules($breakpoint) {
  .ds-flex-item {
    flex: var(--ds-flex-item-flex-#{$breakpoint});
    min-width: var(--ds-flex-item-min-width-#{$breakpoint});
    max-width: var(--ds-flex-item-max-width-#{$breakpoint});
    order: var(--ds-flex-item-order-#{$breakpoint});
  }

  // Stacked items are laid out along the vertical axis, so the size becomes their width.
  .ds-flex-row-column-#{$breakpoint} > .ds-flex-item {
    flex: 0 0 auto;
    width: var(--ds-flex-item-width-#{$breakpoint});
  }

  // A pinned item has nothing to stick to once the row stops scrolling sideways.
  .ds-flex-row-column-#{$breakpoint} > .ds-flex-item-sticky {
    position: static;
    box-shadow: none;
  }
}

@include mobile {
  @include breakpoint-rules('mobile');
}

@include laptop {
  @include breakpoint-rules('laptop');
}

@include desktop {
  @include breakpoint-rules('desktop');
}
</style>
